import { decode } from "html-entities"

/** Fortune Sheet's frozen setting: rows and columns 0..focus stay on screen. */
export type FortuneFrozen = {
  type: "rangeRow" | "rangeColumn" | "rangeBoth"
  range: { row_focus: number; column_focus: number }
}

const attribute = (tag: string, name: string): string | undefined =>
  tag.match(new RegExp(`\\s${name}="([^"]*)"`))?.[1]

const split = (pane: string, name: string): number =>
  Math.max(0, Math.round(parseFloat(attribute(pane, name) ?? "0")) || 0)

/**
 * The frozen panes of an unzipped workbook, per sheet name, as Fortune Sheet
 * takes them. The xlsx transform drops them, so a frozen header scrolled away
 * here while Excel and the new editor keep it. Read like the new editor does
 * (`ExcelEditor/parser/worksheet.ts`): the first sheet view's pane, only in a
 * frozen state; a plain split is not a freeze.
 */
export function readFrozenPanes(
  files: Record<string, unknown>,
): Map<string, FortuneFrozen> {
  const frozen = new Map<string, FortuneFrozen>()
  const workbook = files["xl/workbook.xml"]
  const rels = files["xl/_rels/workbook.xml.rels"]
  if (typeof workbook !== "string" || typeof rels !== "string") return frozen

  const targets = new Map<string, string>()
  for (const [tag] of rels.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = attribute(tag, "Id")
    const target = attribute(tag, "Target")
    if (id && target)
      targets.set(id, target.startsWith("/") ? target.slice(1) : `xl/${target}`)
  }

  for (const [tag] of workbook.matchAll(/<sheet\b[^>]*>/g)) {
    const name = attribute(tag, "name")
    const id = attribute(tag, "r:id")
    const xml = id ? files[targets.get(id) ?? ""] : undefined
    if (!name || typeof xml !== "string") continue
    const sheetView = xml.match(
      /<sheetView\b[^>]*?(?:\/>|>[\s\S]*?<\/sheetView>)/,
    )
    const pane = sheetView?.[0].match(/<pane\b[^>]*>/)?.[0]
    const state = pane && attribute(pane, "state")
    if (!pane || (state !== "frozen" && state !== "frozenSplit")) continue
    const rows = split(pane, "ySplit")
    const columns = split(pane, "xSplit")
    if (rows === 0 && columns === 0) continue
    frozen.set(decode(name), {
      type: rows && columns ? "rangeBoth" : rows ? "rangeRow" : "rangeColumn",
      range: {
        row_focus: Math.max(0, rows - 1),
        column_focus: Math.max(0, columns - 1),
      },
    })
  }
  return frozen
}
